import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Lock, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const lastUpdated = 'July 7, 2026';
  const contactEmail = 'arynnlittlejohn@icloud.com';

  return (
    <div className="min-h-screen bg-vault" data-testid="privacy-policy-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-[#9333EA] flex items-center justify-center">
                <Lock className="h-4 w-4 text-white" />
              </div>
              <span className="font-heading text-lg font-bold text-white tracking-tight">FutureVault</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-[#A19DB0] hover:text-white hover:bg-white/5" data-testid="privacy-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="font-heading text-4xl font-black text-white tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-[#A19DB0] text-sm mb-10">Last updated: {lastUpdated}</p>

        <div className="space-y-8 text-[#D4D1E0] leading-relaxed">
          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">1. Introduction</h2>
            <p>
              FutureVault LLC, a California Limited Liability Company (&quot;FutureVault&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;), provides a savings lockbox service that helps users save money by
              locking funds until a user-chosen unlock date. This Privacy Policy explains what information we
              collect, how we use it, and the rights you have over your information when you use FutureVault
              through our website (future-vault.com) or our mobile applications (collectively, the &quot;Service&quot;).
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">2. Information We Collect</h2>
            <p className="mb-3">We collect the following categories of information:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><span className="text-white font-semibold">Account information:</span> name, email address, and password (hashed).</li>
              <li><span className="text-white font-semibold">Savings data:</span> vault names, target amounts, unlock dates, and deposit history.</li>
              <li><span className="text-white font-semibold">Financial information:</span> we do not store your bank account or card numbers. Payment processing and bank connections are handled by Stripe. We store identifiers issued by Stripe (such as your Stripe Connect account ID and Checkout session IDs).</li>
              <li><span className="text-white font-semibold">Subscription information:</span> tier (free/premium) and subscription status.</li>
              <li><span className="text-white font-semibold">Device & usage data:</span> IP address, browser/device type, and basic analytics about how you use the Service.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>To create and maintain your account.</li>
              <li>To process deposits, hold funds via Stripe Connect, and release funds on your chosen unlock date.</li>
              <li>To send transactional emails (sign-up confirmation, password resets, deposit/withdrawal receipts, unlock reminders).</li>
              <li>To operate, secure, and improve the Service.</li>
              <li>To comply with legal obligations, including anti-fraud and financial regulations.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">4. Third-Party Services</h2>
            <p className="mb-3">We share limited information with trusted third parties only as needed to operate the Service:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><span className="text-white font-semibold">Stripe</span> — payment processing, Stripe Connect bank onboarding, and payouts. See <a href="https://stripe.com/privacy" className="text-[#9333EA] underline" target="_blank" rel="noreferrer">Stripe&apos;s Privacy Policy</a>.</li>
              <li><span className="text-white font-semibold">RevenueCat</span> — subscription management for iOS Premium billing. See <a href="https://www.revenuecat.com/privacy" className="text-[#9333EA] underline" target="_blank" rel="noreferrer">RevenueCat&apos;s Privacy Policy</a>.</li>
              <li><span className="text-white font-semibold">Resend</span> — sending transactional emails on our behalf.</li>
              <li><span className="text-white font-semibold">MongoDB Atlas</span> — encrypted database hosting.</li>
              <li><span className="text-white font-semibold">Apple / Google</span> — if you use the mobile app, in-app purchases for Premium subscriptions are processed through Apple or Google per their policies.</li>
            </ul>
            <p className="mt-3 text-white font-semibold">We do not sell or rent your personal information to any third party.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">5. Data Security</h2>
            <p>
              We protect your information with industry-standard safeguards, including HTTPS/TLS encryption in
              transit, encrypted database storage, password hashing (bcrypt), and JWT-based authentication.
              No system is 100% secure; if we become aware of a breach that affects your account we will
              notify you promptly as required by law.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">6. Data Retention</h2>
            <p>
              We retain your account and vault data for as long as your account is active. If you delete your
              account, we will delete or anonymize your personal data within 90 days, except where we are
              required by law or financial regulation to retain certain transaction records.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">7. Your Rights</h2>
            <p className="mb-3">Depending on where you live, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Access, correct, or delete the personal information we hold about you.</li>
              <li>Request a copy of your data in a portable format.</li>
              <li>Withdraw consent or object to certain processing.</li>
              <li>Lodge a complaint with your local data-protection authority.</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, or to delete your account, open the FutureVault mobile app and go to{' '}
              <span className="text-white font-semibold">Profile &rsaquo; Delete Account</span>, or email us at{' '}
              <a href={`mailto:${contactEmail}`} className="text-[#9333EA] underline">{contactEmail}</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">7a. Your California Privacy Rights (CCPA)</h2>
            <p className="mb-3">
              If you are a California resident, the California Consumer Privacy Act (CCPA) gives you additional
              rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><span className="text-white font-semibold">Right to know</span> what personal information we collect, use, and disclose about you.</li>
              <li><span className="text-white font-semibold">Right to delete</span> personal information we hold about you (subject to legal retention requirements for financial records).</li>
              <li><span className="text-white font-semibold">Right to correct</span> inaccurate personal information.</li>
              <li><span className="text-white font-semibold">Right to opt out</span> of the sale or sharing of your personal information — <span className="text-white">we do not sell or share your personal information</span>, so nothing changes when you exercise this right, but you are entitled to make the request.</li>
              <li><span className="text-white font-semibold">Right to non-discrimination</span> — we will not deny service, charge different prices, or reduce quality because you exercised any CCPA right.</li>
            </ul>
            <p className="mt-3">
              To exercise any California right, email us at{' '}
              <a href={`mailto:${contactEmail}`} className="text-[#9333EA] underline">{contactEmail}</a>{' '}
              with the subject line &quot;California Privacy Request&quot;. We will verify your identity and respond
              within 45 days.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">8. Children&apos;s Privacy</h2>
            <p>
              FutureVault is not directed to children under 13 (or under 16 in the EEA/UK). We do not
              knowingly collect personal information from children. If you believe a child has provided us
              information, please contact us so we can delete it.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">9. International Users</h2>
            <p>
              FutureVault is operated from the United States. If you access the Service from outside the US,
              your information may be transferred to, stored, and processed in the US and other countries where
              our service providers operate.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Material changes will be communicated by
              email or through a notice in the Service. The &quot;Effective date&quot; above will always reflect the
              latest version.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">11. Contact Us</h2>
            <p>
              Questions about this Privacy Policy or our data practices? Contact us at:
            </p>
            <address className="not-italic mt-3 text-[#D7CCE6] leading-relaxed" data-testid="privacy-contact-address">
              FutureVault LLC<br />
              212 Skyline Ridge Loop<br />
              Sacramento, CA 95834<br />
              Email: <a href={`mailto:${contactEmail}`} className="text-[#9333EA] underline">{contactEmail}</a>
            </address>
          </section>
        </div>

        <div className="mt-12 flex flex-wrap gap-4 text-sm text-[#A19DB0]">
          <Link to="/terms" className="hover:text-white" data-testid="privacy-to-terms-link">Terms of Service</Link>
          <span>·</span>
          <Link to="/refunds" className="hover:text-white" data-testid="privacy-to-refunds-link">Refund Policy</Link>
          <span>·</span>
          <Link to="/support" className="hover:text-white" data-testid="privacy-to-support-link">Support</Link>
        </div>
      </main>
    </div>
  );
}
